import { useToast } from '@cian/ui-kit';
import { equals } from 'ramda';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { landingApi } from '../../api';
import { SHORT_FORM_WEB_EXPERIMENT } from '../../constants/abExperiments';
import { OFFERS_FETCH_ERROR_TOAST_TEXT } from '../../constants/offers';
import {
  setActionUrl,
  setFinancePlatformEnabled,
  setOffersForAnalytic,
  setOffersLength,
  setShortFormGroup,
} from '../../redux/landingSlice';
import {
  selectCalculatorDefaultValues,
  selectPrefetchedOffersData,
  selectPrefetchedOffersRequest,
  selectUtmTags,
} from '../../selectors/landing';
import { ICalculatorMcfValues } from '../../types/features';
import { buildGetOfferListingRequest, getMapOffers } from '../../utils/offers';
import { getOffersForAnalytic } from '../../utils/tracking/utils/getOffersForAnalytic';

export const useGetMappedOffers = (calculatorValues: ICalculatorMcfValues) => {
  const dispatch = useDispatch();
  const toast = useToast({ placement: 'bottom-right' });
  const prefetchedData = useSelector(selectPrefetchedOffersData);
  const prefetchedRequest = useSelector(selectPrefetchedOffersRequest);
  const calculatorDefaultValues = useSelector(selectCalculatorDefaultValues);
  const utmTags = useSelector(selectUtmTags);

  const requestObject = useMemo(
    () => buildGetOfferListingRequest(calculatorValues, calculatorDefaultValues ?? {}),
    [calculatorValues, calculatorDefaultValues],
  );

  const requestMatchesPrefetch = prefetchedRequest != null && equals(requestObject, prefetchedRequest);

  const usePrefetched = prefetchedData != null && requestMatchesPrefetch;

  const queryResult = landingApi.endpoints.getOfferListingV2.useQuery(requestObject, {
    skip: usePrefetched,
  });

  const data = usePrefetched ? prefetchedData : queryResult.data;
  const isFetching = usePrefetched ? false : queryResult.isFetching;

  useEffect(() => {
    if (usePrefetched || isFetching || !queryResult.isError) {
      return;
    }

    toast.open({
      type: 'info',
      text: OFFERS_FETCH_ERROR_TOAST_TEXT,
      dataTestId: 'offers-fetch-error-toast',
    });
  }, [isFetching, queryResult.isError, toast, usePrefetched]);

  const mappedOffers = useMemo(() => (data && data.offers ? getMapOffers(data.offers, utmTags) : []), [data, utmTags]);

  // Переносим dispatch вызовы в useEffect, чтобы избежать обновления состояния во время рендеринга
  useEffect(() => {
    if (data && 'actionUrl' in data) {
      dispatch(setActionUrl(data.actionUrl as string));
    }

    if (data && data.meta) {
      dispatch(setFinancePlatformEnabled(data.meta.financePlatformEnabled));

      /** Поле experiments появляется в сгенерированном типе только при генерации из ветки бэка,
          поэтому читается локальным типом — код собирается и до влития CD-279987, и после. */
      const { experiments } = data.meta as { experiments?: Record<string, string | undefined> };
      const shortFormGroup = experiments?.[SHORT_FORM_WEB_EXPERIMENT];

      if (shortFormGroup) {
        dispatch(setShortFormGroup(shortFormGroup));
      }
    }

    const offersCount = mappedOffers.length;

    dispatch(setOffersLength(offersCount || null));
    dispatch(setOffersForAnalytic(offersCount ? getOffersForAnalytic(mappedOffers) : null));
  }, [data, mappedOffers, dispatch]);

  return { mappedOffers, isFetching };
};
